import { useEffect, useCallback, useRef } from 'react';

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_BEFORE_TIMEOUT_MS = 5 * 60 * 1000; // Show warning 5 minutes before timeout
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
const STORAGE_KEY = 'lastActivityTime';

/**
 * Session timeout hook for CJIS compliance
 * Auto-logs out users after 30 minutes of inactivity
 */
export function useSessionTimeout({ onTimeout, onWarning, enabled = true }) {
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const lastActivityRef = useRef(null);

  // Initialize lastActivityRef on mount
  useEffect(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Update last activity time in localStorage (for cross-tab sync)
  const updateActivity = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    localStorage.setItem(STORAGE_KEY, now.toString());
  }, []);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
  }, []);

  // Reset the timeout timer
  const resetTimeout = useCallback(() => {
    if (!enabled) return;

    clearTimers();
    updateActivity();

    // Set warning timer (5 minutes before timeout)
    warningRef.current = setTimeout(() => {
      if (onWarning) {
        onWarning(WARNING_BEFORE_TIMEOUT_MS / 1000 / 60); // Pass remaining minutes
      }
    }, SESSION_TIMEOUT_MS - WARNING_BEFORE_TIMEOUT_MS);

    // Set logout timer (30 minutes)
    timeoutRef.current = setTimeout(() => {
      if (onTimeout) {
        onTimeout();
      }
    }, SESSION_TIMEOUT_MS);
  }, [enabled, clearTimers, updateActivity, onTimeout, onWarning]);

  // Check if session has expired (for page load/refresh)
  const checkSessionExpiry = useCallback(() => {
    const lastActivity = localStorage.getItem(STORAGE_KEY);
    if (lastActivity) {
      const elapsed = Date.now() - parseInt(lastActivity, 10);
      if (elapsed >= SESSION_TIMEOUT_MS) {
        // Session expired while away
        return true;
      }
    }
    return false;
  }, []);

  // Get remaining time in seconds
  const getRemainingTime = useCallback(() => {
    const lastActivity = localStorage.getItem(STORAGE_KEY);
    if (lastActivity) {
      const elapsed = Date.now() - parseInt(lastActivity, 10);
      const remaining = SESSION_TIMEOUT_MS - elapsed;
      return Math.max(0, Math.floor(remaining / 1000));
    }
    return SESSION_TIMEOUT_MS / 1000;
  }, []);

  // Handle storage events (cross-tab sync)
  const handleStorageChange = useCallback((event) => {
    if (event.key === STORAGE_KEY && event.newValue) {
      // Activity detected in another tab, reset our timers
      resetTimeout();
    }
  }, [resetTimeout]);

  // Handle activity events
  const handleActivity = useCallback(() => {
    resetTimeout();
  }, [resetTimeout]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    // Check if session already expired
    if (checkSessionExpiry()) {
      if (onTimeout) {
        onTimeout();
      }
      return;
    }

    // Initialize activity time
    updateActivity();
    resetTimeout();

    // Add activity event listeners
    ACTIVITY_EVENTS.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Listen for storage changes (cross-tab sync)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [enabled, clearTimers, checkSessionExpiry, updateActivity, resetTimeout, handleActivity, handleStorageChange, onTimeout]);

  return {
    resetTimeout,
    getRemainingTime,
    checkSessionExpiry,
    clearTimers
  };
}

export default useSessionTimeout;
